import React, { useState, useEffect } from 'react';
import { useAuth, useUser, SignIn } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../lib/api';

export default function LinkDevice() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState('');

  // Auto-fill from ?code= search param
  useEffect(() => {
    const paramCode = searchParams.get('code');
    if (paramCode) {
      setCode(paramCode.toUpperCase());
    }
  }, [searchParams]);

  const formatCode = (value) => {
    // Strip non-alphanumeric, uppercase, limit to 8 chars (excluding hyphen)
    const raw = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 8);
    if (raw.length > 4) {
      return raw.slice(0, 4) + '-' + raw.slice(4);
    }
    return raw;
  };

  const handleCodeChange = (e) => {
    setCode(formatCode(e.target.value));
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rawCode = code.replace(/-/g, '');
    if (rawCode.length !== 8) return;

    setStatus('loading');
    setErrorMessage('');

    try {
      await apiClient.approveCliAuth(code);
      setStatus('success');
    } catch (error) {
      setStatus('error');
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 404 || (typeof detail === 'string' && detail.toLowerCase().includes('expired'))) {
        setErrorMessage('Invalid or expired code. Please try again from your terminal.');
      } else if (error?.response?.status === 409 || (typeof detail === 'string' && detail.toLowerCase().includes('already'))) {
        setErrorMessage('This code has already been used.');
      } else {
        setErrorMessage(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
      }
    }
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white">Sign in to link your agent</h2>
            <p className="mt-2 text-sm text-zinc-500">
              Sign in to your Kalibr account to continue.
            </p>
          </div>
          <SignIn
            routing="hash"
            afterSignInUrl={`/link${searchParams.get('code') ? `?code=${searchParams.get('code')}` : ''}`}
          />
        </div>
      </div>
    );
  }

  const orgName = user?.organizationMemberships?.[0]?.organization?.name;
  const displayName = orgName || 'your account';

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white">Agent Linked</h2>
          <p className="text-zinc-400">
            Agent linked to <span className="text-white font-medium">{displayName}</span>. You can close this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-white">Link an Agent</h1>
          <p className="text-sm text-zinc-500">
            Enter the code from your terminal to connect an agent to your account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            value={code}
            onChange={handleCodeChange}
            placeholder="ABCD-WXYZ"
            maxLength={9}
            autoFocus
            className="w-full bg-[#141416] border border-white/[0.08] rounded-lg px-4 py-3 text-center text-xl font-mono tracking-widest text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/[0.16] transition-colors"
          />

          {status === 'error' && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400 text-center">{errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={code.replace(/-/g, '').length !== 8 || status === 'loading'}
            className="w-full bg-white text-black font-medium rounded-lg px-4 py-2.5 text-sm hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {status === 'loading' ? 'Linking...' : 'Link Agent'}
          </button>
        </form>
      </div>
    </div>
  );
}
